import { Component, OnInit, Input } from "@angular/core";
import { DatePipe } from "@angular/common";
import { Router, NavigationStart } from '@angular/router';
import { BehaviorSubject, Subscription } from "rxjs";
import { filter, finalize } from "rxjs/operators";

import { AuthService } from "../../../../services/auth.service";
import { OrganizationService } from "../../../services/organization.service";
import { DocumentManagementService } from "../../../services/document-management.service";

import { DomainValue, DocumentWithLink } from "../../../models/document-tag.model";
import { Organization } from "../../../models/organization.model";


@Component({
    selector: "organization-files",
    templateUrl: "./organization-files.component.html",
    styleUrls: ["../../../../form-controls/components/file-upload/file-upload.component.scss", "./organization-files.component.scss"],
    standalone: false
})
export class OrganizationFilesComponent implements OnInit {

    accordionSections: Map<string, DocumentWithLink[]>;
    filesLoading$ = new BehaviorSubject<boolean>(true);
    filteredTagTypes = ["Document Type", "Owner Number", "Owner Name"];
    hasNoFiles: boolean = null;
    @Input() organization: Organization;
    sectionsExpanded: Map<string, boolean>;
    tags: DomainValue[];
    dataSubscriptions: Subscription = new Subscription();

    get organizationHasNoFiles(): boolean {
        if (this.hasNoFiles === null) {
            let innerBool: boolean = true;
            this.accordionSections.forEach(docs => {
                if (docs.length) innerBool = false;
            });
            this.hasNoFiles = innerBool;
        }
        return this.hasNoFiles;
    }

    get caption(): string {
        let msg = "Organization Files";
        if (this.organization != null && this.organization.name != null && this.organization.name.length > 0) {
            msg = `${this.organization.name} Files`;
        }
        return msg;
    }

    constructor(
        public authService: AuthService,
        public readonly orgService: OrganizationService,
        public readonly docService: DocumentManagementService,
        public readonly datePipe: DatePipe,
        private router: Router
    ) { }

    ngOnInit() {
        this.sectionsExpanded = new Map<string, boolean>();
        if (!this.organization) {
            this.dataSubscriptions.add(this.orgService.getOrganization(this.authService.User.organizationId).subscribe(org => {
                this.organization = org;
                this.loadDocuments();
            }, (error) => {
                console.error(error);
            }));
        } else {
            this.loadDocuments();
        }

        this.router.events.pipe(filter(e => e instanceof NavigationStart)).subscribe((routerEvent: NavigationStart) => {
            this.dataSubscriptions.unsubscribe();
        });
    }

    ngOnDestroy() {
        this.dataSubscriptions.unsubscribe();
    }

    accordion(section: string): void {
        const current = this.isSectionExpanded(section);
        this.sectionsExpanded.set(section, !current);
    }

    dateFormatter(date: string): string {
        return this.datePipe.transform(date, "MM/dd/yyyy");
    }

    getSectionDocuments(section: string): DocumentWithLink[] {
        const docs = this.accordionSections.get(section);
        if (docs) {
            return docs;
        }
        return [];
    }

    isSectionExpanded(section: string): boolean {
        const result = this.sectionsExpanded.get(section);
        if (result !== undefined && result != null) {
            return result;
        }
        return false;
    }

    loadDocuments(): void {
        this.tags = [];
        this.accordionSections = new Map<string, DocumentWithLink[]>();
        this.docService.documentTypes.subscribe(data => {
            this.tags = data;
            for (let t = 0; t < this.tags.length; t++) {
                const isExpanded = this.sectionsExpanded.get(this.tags[t].value);
                if (isExpanded === undefined || isExpanded === null) {
                    this.sectionsExpanded.set(this.tags[t].value, false);
                }
                this.accordionSections.set(this.tags[t].value, []);
            }
            if (this.tags.length > 0) {
                const historicOwnerNumber = [];
                if (this.organization.historicalOwnerNumber !== undefined &&
                    this.organization.historicalOwnerNumber !== null) {
                    historicOwnerNumber.push(this.organization.historicalOwnerNumber);
                }
                this.orgService.getOrganizationFiles(this.organization.id, historicOwnerNumber).subscribe(filedata => {
                    for (let f = 0; f < filedata.length; f++) {
                        const docTypeTag = filedata[f].tags.find(t => t.tag === this.filteredTagTypes[0]);
                        let docType: string;
                        if (docTypeTag.value instanceof Object) {
                            docType = docTypeTag.value.value;
                        } else {
                            docType = docTypeTag.value;
                        }

                        filedata[f].tags = filedata[f].tags.filter(t => this.filteredTagTypes.indexOf(t.tag) === -1);
                        let typeFiles = this.accordionSections.get(docType);

                        if (typeFiles === undefined) {
                            if (docType)
                                this.accordionSections.set(docType, [filedata[f]]);
                            else
                                this.accordionSections.set("Unknown", [filedata[f]]);
                        } else {
                            typeFiles.push(filedata[f]);
                            this.accordionSections.set(docType, typeFiles);
                        }

                        
                    }
                    this.filesLoading$.next(false);
                },
                    error => {
                        this.filesLoading$.next(false);
                        console.error(error);
                    });
            }
        }, error => {
            console.error(error);
        });
    }

    refreshLinks(): void {
        this.filesLoading$.next(true);
        this.loadDocuments();
    }

    showAccordionSection(section: string): boolean {
        const fileList = this.accordionSections.get(section);
        if (fileList) {
            return fileList.length > 0;
        }
        return false;
    }

    sort(section: string, field: string, direction: string): void {
        const collection = this.accordionSections.get(section);
        collection.sort(this.compareElements(field, direction));
        this.accordionSections.set(section, collection);
    }

    compareElements(property: string, direction: string): (a, b) => number {
        switch (property) {
            case "name":
                return (a, b) => {
                    if (a.name > b.name) {
                        if (direction === "asc") return 1;
                        else
                            return -1;
                    } else if (a.name < b.name) {
                        if (direction === "asc") return -1;
                        else return 1;
                    } else return 0;
                }
            case "date":
                return (a, b) => {
                    if (a.auditDate > b.auditDate) {
                        if (direction === "asc") return 1;
                        else
                            return -1;
                    } else if (a.auditDate < b.auditDate) {
                        if (direction === "asc") return -1;
                        else return 1;
                    } else return 0;
                };
            case "tags":
                return (a, b) => {
                    const tagAFirst = a.tags[0];
                    const tagBFirst = b.tags[0];

                    if (tagAFirst > tagBFirst) {
                        if (direction === "asc") return 1;
                        else
                            return -1;
                    } else if (tagAFirst < tagBFirst) {
                        if (direction === "asc") return -1;
                        else return 1;
                    } else return 0;
                };
        }
    }
}
